// src/pages/guides/ConfigurationDriftGuide.js
// Configuration Drift Pillar Guide (G1) - SEO Guides Program

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function ConfigurationDriftGuide() {
  const startTrial = useTrialCta('guide_trial:configuration-drift');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Configuration Drift: Why Security Breaks Quietly",
    "description": "Learn how external configuration drift — DNS changes, new ports, TLS degradation — creates security gaps attackers exploit. 7-point detection checklist.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2026-03-06T00:00:00-05:00",
    "dateModified": "2026-03-06T00:00:00-05:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/configuration-drift",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/configuration-drift",
    "keywords": ["configuration drift security", "what is configuration drift", "external drift detection", "security configuration monitoring", "attack surface drift"]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "Configuration Drift" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Configuration Drift: Why Security Breaks | DriftAlarm"
        description="Learn how external configuration drift — DNS changes, new ports, TLS degradation — creates security gaps attackers exploit. 7-point detection checklist."
        path="/guides/configuration-drift"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/configuration-drift')].filter(Boolean)}
      />

      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>Configuration Drift: Why Security Breaks Quietly</h1>
          <GuideByline path="/guides/configuration-drift" />
          <GuideAnswer path="/guides/configuration-drift" />
          <p className="guide-hero-intro">
            Your external attack surface changes every day. DNS records get updated. Ports open during maintenance
            and never close. TLS certificates expire without anyone noticing. These incremental, often undocumented
            changes are called <strong>configuration drift</strong>. This guide explains what external
            configuration drift is, why it matters more
            than internal drift for internet-facing assets, and how to detect it before attackers do.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>What configuration drift is and why it creates security gaps</li>
              <li>Why external drift is fundamentally different from internal drift</li>
              <li>The 5 most dangerous types of external drift</li>
              <li>How drift leads to breaches — 3 real-world patterns</li>
              <li>A 7-point drift monitoring checklist you can implement today</li>
            </ul>
          </div>

          <span className="guide-card-read-time" style={{ display: 'inline-block', marginBottom: '20px' }}>18 min read</span>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/how-it-works" className="da-btn da-btn--secondary">See our methodology</Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#what-is-drift">What Is Configuration Drift?</a></li>
            <li><a href="#external-vs-internal">External vs Internal Drift</a></li>
            <li><a href="#five-types">5 Most Dangerous Types of External Drift</a></li>
            <li><a href="#breach-patterns">How Drift Leads to Breaches</a></li>
            <li><a href="#manual-vs-automated">Manual vs Automated Drift Detection</a></li>
            <li><a href="#checklist">7-Point Drift Monitoring Checklist</a></li>
            <li><a href="#how-driftalarm-detects">How DriftAlarm Detects Drift</a></li>
            <li><a href="#related-guides">Related Security Guides</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        {/* Section 1: What Is Configuration Drift? */}
        <section id="what-is-drift" className="guide-section">
          <h2>What Is Configuration Drift?</h2>
          <p>
            Configuration drift occurs when the actual state of a system diverges from its intended, documented,
            or baseline state. In security terms, it means your infrastructure no longer matches what you think
            it looks like — and the gap between perception and reality is where attackers operate.
          </p>
          <p>
            Drift happens naturally. Every time someone updates a DNS record, opens a firewall port for
            troubleshooting, swaps a load balancer configuration, or lets an SSL certificate auto-renew
            to a weaker profile, the system drifts slightly from its known-good state. Individually, these
            changes are minor. Collectively, over weeks and months, they create an attack surface that no
            one fully understands.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">Drift Is Not a Vulnerability — It Creates Them</div>
            <div className="callout-content">
              <p>
                Configuration drift is a process, not a single event. A DNS record change is not inherently
                dangerous. But when that change points a subdomain to a decommissioned server, or when a
                port opens and no one tracks it, the drift becomes a vulnerability — one that traditional
                scanners often miss because they scan for known CVEs, not for changes from a baseline.
              </p>
            </div>
          </div>

          <p>
            The challenge with drift is that no single change triggers an alarm. There is no CVE for "you
            opened port 8443 three weeks ago and forgot about it." There is no signature for "your TLS
            configuration silently downgraded during a certificate renewal." Drift is the slow accumulation
            of small decisions and oversights that, taken together, erode your security posture.
          </p>
        </section>

        {/* Section 2: External vs Internal Drift */}
        <section id="external-vs-internal" className="guide-section">
          <h2>Why External Drift Is Different from Internal Drift</h2>
          <p>
            Most discussions of configuration drift focus on internal infrastructure — Terraform state files
            drifting from declared configurations, Ansible playbooks falling out of sync, or Docker images
            accumulating undocumented changes. Those are real problems, but they exist behind your firewall,
            protected by network segmentation and access controls.
          </p>
          <p>
            <strong>External drift is different because it is immediately visible to attackers.</strong> When
            your internet-facing configuration changes, every scanner on the internet — internet-wide
            scanning services
            and countless attacker-operated botnets — can see it within hours. There is no firewall between
            the drift and the threat.
          </p>

          <div className="callout callout-warning">
            <div className="callout-title">External Drift Has Zero Buffer Time</div>
            <div className="callout-content">
              <p>
                Internal drift gives you time. A misconfigured server on an internal VLAN might sit unnoticed
                for months without consequence because attackers cannot reach it directly. External drift offers
                no such buffer. A new open port on a public IP is discoverable by automated scanners within
                minutes. The window between drift and exploitation can be measured in hours, not months.
              </p>
            </div>
          </div>

          <p>
            External configuration drift includes changes to anything visible from the public internet: DNS
            records, open ports and services, TLS/SSL configurations, HTTP headers and response behaviors,
            technology stack versions, domain registration details, and cloud resource access policies. These
            are the elements that define your external attack surface, and when they change without tracking,
            you lose situational awareness.
          </p>
        </section>

        {/* Section 3: 5 Most Dangerous Types of External Drift */}
        <section id="five-types" className="guide-section">
          <h2>The 5 Most Dangerous Types of External Configuration Drift</h2>
          <p>
            Not all drift is equally dangerous. Based on real-world breach patterns, these five categories
            of external drift create the most exploitable gaps.
          </p>

          <h3>1. DNS Record Drift</h3>
          <div className="callout callout-critical">
            <div className="callout-title">High Risk: Subdomain Takeover</div>
            <div className="callout-content">
              <p>
                DNS records that point to decommissioned cloud resources (dangling CNAMEs) can be claimed by
                attackers, allowing them to serve content on your subdomain. This enables phishing, cookie
                theft, and reputation damage — all under your own domain name.
              </p>
            </div>
          </div>
          <p>
            DNS drift includes new subdomains appearing without documentation, records pointing to IP
            addresses that no longer belong to you, MX record changes that reroute email, and TXT record
            modifications that affect SPF/DKIM validation. Because DNS is the foundation of your external
            identity, any undocumented change here can have cascading effects.
          </p>

          <h3>2. TLS/SSL Configuration Drift</h3>
          <div className="callout callout-warning">
            <div className="callout-title">Silent Degradation</div>
            <div className="callout-content">
              <p>
                TLS drift often happens during certificate renewals or load balancer updates. A certificate
                renews successfully, but the new configuration supports TLS 1.0 when the old one did not.
                Cipher suites weaken. Certificate chains become incomplete. These changes are invisible to
                users but visible to attackers.
              </p>
            </div>
          </div>
          <p>
            TLS drift patterns include expired certificates that were not renewed, downgraded protocol
            versions (TLS 1.2 to TLS 1.0), weak cipher suites introduced during server updates, missing
            intermediate certificates causing chain validation failures, and certificate subject mismatches
            after infrastructure migrations.
          </p>

          <h3>3. Open Port and Service Drift</h3>
          <div className="callout callout-critical">
            <div className="callout-title">The "Temporary" Port That Stayed Open</div>
            <div className="callout-content">
              <p>
                Ports opened for maintenance, debugging, or deployment frequently remain open after the work
                is done. A database port opened for a one-time migration. An admin panel exposed for remote
                troubleshooting. A debug endpoint enabled during a production issue. These "temporary"
                exceptions become permanent attack surface.
              </p>
            </div>
          </div>
          <p>
            Port drift is particularly dangerous because every new open port is a new entry point.
            Management ports (SSH, RDP, database ports) exposed without documentation represent some of the
            highest-risk drift events. Service version changes on existing ports — such as an Apache upgrade
            that introduces a new vulnerability — also fall into this category.
          </p>

          <h3>4. Technology Stack Drift</h3>
          <div className="callout callout-info">
            <div className="callout-title">Version Changes Create New Attack Surface</div>
            <div className="callout-content">
              <p>
                When web servers, CMS platforms, frameworks, or libraries change versions, new vulnerabilities
                may be introduced. Technology stack drift is the gap between what your asset register says is
                running and what is actually deployed. Software updates, container image changes, and plugin
                installations all contribute to tech stack drift.
              </p>
            </div>
          </div>
          <p>
            Common tech stack drift scenarios include WordPress or CMS plugins updated without security review,
            web server software reaching end-of-life without planned migration, framework version changes that
            introduce known CVEs, and development dependencies leaking into production builds.
          </p>

          <h3>5. Domain Registration Drift</h3>
          <div className="callout callout-warning">
            <div className="callout-title">Ownership and Expiry Risks</div>
            <div className="callout-content">
              <p>
                Domain registration changes include approaching expiration dates, WHOIS contact changes,
                nameserver modifications, and registrar transfers. An expired domain can be registered by
                an attacker and used for phishing. Nameserver changes can redirect all traffic. These are
                foundational changes that affect every service on the domain.
              </p>
            </div>
          </div>
          <p>
            Registration drift is easy to overlook because domain management is often handled by a single
            person or team, and renewal cycles are annual. A domain that was registered by an employee who
            has since left, with a personal email as the recovery contact, represents a significant and
            growing risk.
          </p>
        </section>

        {/* Section 4: How Drift Leads to Breaches */}
        <section id="breach-patterns" className="guide-section">
          <h2>How Configuration Drift Leads to Breaches</h2>
          <p>
            Drift does not cause breaches directly. It creates the conditions that make breaches possible.
            Here are three composite example scenarios, drawn from common real-world patterns.
          </p>

          <div className="example-box">
            <div className="example-title">Pattern 1: The Forgotten Staging Server</div>
            <div className="example-content">
              <p>
                A development team deploys a staging environment on a cloud VM with a public IP. The server
                runs an older version of the application with debug endpoints enabled and default credentials.
                It was meant to be temporary — two weeks for a client demo. Eighteen months later, the VM is
                still running. No one monitors it. No one patches it. An attacker discovers the server through
                a subdomain enumeration scan, logs in with default credentials, and uses the staging server as
                a pivot point to access internal resources via a shared VPN connection.
              </p>
              <p>
                <strong>The drift:</strong> A new internet-facing asset appeared without being added to the
                asset register or monitoring scope. Over time, it drifted further as patches were not applied.
              </p>
            </div>
          </div>

          <div className="example-box">
            <div className="example-title">Pattern 2: The TLS Downgrade</div>
            <div className="example-content">
              <p>
                A company migrates its primary web application to a new load balancer. The migration succeeds
                and the site works correctly. However, the new load balancer's default TLS configuration
                supports TLS 1.0 and weak cipher suites that the previous configuration explicitly disabled.
                Six months later, a compliance audit flags the issue — but in the interim, the weakened TLS
                configuration made the application vulnerable to downgrade attacks that could have exposed
                session tokens.
              </p>
              <p>
                <strong>The drift:</strong> TLS configuration silently degraded during an infrastructure
                change. No baseline comparison was performed after the migration.
              </p>
            </div>
          </div>

          <div className="example-box">
            <div className="example-title">Pattern 3: The DNS Record That Pointed Nowhere</div>
            <div className="example-content">
              <p>
                A marketing team launches a campaign on a cloud-hosted microsite at <code>promo.example.com</code>,
                pointed via CNAME to a cloud provider endpoint. The campaign ends, and the cloud resource is
                decommissioned — but the DNS record remains. An attacker notices the dangling CNAME, provisions
                a new resource at the same cloud endpoint, and now controls content served under the company's
                domain. They deploy a credential harvesting page that inherits the parent domain's cookies and
                SSL certificate trust.
              </p>
              <p>
                <strong>The drift:</strong> A DNS record persisted after the resource it pointed to was removed.
                No process existed to audit DNS records when cloud resources were decommissioned.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: Manual vs Automated Detection */}
        <section id="manual-vs-automated" className="guide-section">
          <h2>Detecting Drift: Manual vs Automated Monitoring</h2>
          <p>
            There are two approaches to detecting configuration drift on your external attack surface:
            periodic manual reviews and continuous automated monitoring. Most organizations start with
            manual processes and evolve toward automation as their surface grows.
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Factor</th>
                  <th>Manual Monitoring</th>
                  <th>Automated Monitoring</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Frequency</strong></td>
                  <td>Monthly or quarterly audits</td>
                  <td>Daily or continuous scanning</td>
                </tr>
                <tr>
                  <td><strong>Coverage</strong></td>
                  <td>Known assets only</td>
                  <td>Known + discovered assets</td>
                </tr>
                <tr>
                  <td><strong>Detection speed</strong></td>
                  <td>Weeks to months</td>
                  <td>Hours to days</td>
                </tr>
                <tr>
                  <td><strong>Baseline tracking</strong></td>
                  <td>Spreadsheet snapshots</td>
                  <td>Automated baseline with diff alerts</td>
                </tr>
                <tr>
                  <td><strong>Cost</strong></td>
                  <td>Staff time (4-8 hours/audit)</td>
                  <td>Tool subscription + setup time</td>
                </tr>
                <tr>
                  <td><strong>Scalability</strong></td>
                  <td>Breaks down past 20-30 assets</td>
                  <td>Scales to thousands of assets</td>
                </tr>
                <tr>
                  <td><strong>False positive handling</strong></td>
                  <td>Human judgment (slow but accurate)</td>
                  <td>Rule-based filtering (fast, tunable)</td>
                </tr>
                <tr>
                  <td><strong>Best for</strong></td>
                  <td>Small environments, initial setup</td>
                  <td>Growing environments, ongoing operations</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            The key difference is not just speed — it is <strong>baseline awareness</strong>. Manual
            monitoring tells you what your surface looks like right now. Automated monitoring tells you
            what changed since last time. That delta — the diff between two scans — is where drift lives,
            and it is the single most actionable signal in attack surface management.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">Start Manual, Automate What Matters</div>
            <div className="callout-content">
              <p>
                If you are building a drift monitoring program from scratch, start with a manual baseline.
                Document your DNS records, open ports, TLS configurations, and running services. Then use
                that baseline as the benchmark for automated monitoring. You cannot detect drift if you do
                not know what "normal" looks like.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: 7-Point Checklist */}
        <section id="checklist" className="guide-section">
          <h2>7-Point Drift Monitoring Checklist</h2>
          <p>
            Whether you use manual processes or automated tools, these seven practices form the foundation
            of effective external drift monitoring. Implement them in order — each builds on the previous.
          </p>

          <div className="step-cards">
            <div className="step-card">
              <div className="step-card-number">1</div>
              <div className="step-card-title">Establish Your Baseline</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Document the current state of every external asset: DNS records, open ports, TLS
                configurations, running services, and technology versions. This is your known-good
                reference point. Without it, you cannot detect drift — only current state.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">2</div>
              <div className="step-card-title">Monitor DNS Changes Daily</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Track new subdomains, modified A/AAAA/CNAME records, MX changes, and TXT record updates.
                DNS drift is often the first indicator of unauthorized changes or decommissioning oversights.
                Flag any DNS record pointing to an IP or hostname outside your known infrastructure.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">3</div>
              <div className="step-card-title">Track Port and Service Changes</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Scan your IP ranges regularly and compare results against your baseline. New open ports,
                closed ports that reopen, and service version changes all represent drift events. Pay
                special attention to management ports (22, 3389, 3306, 5432) appearing on public IPs.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">4</div>
              <div className="step-card-title">Audit TLS/SSL Configuration Weekly</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Check certificate expiration dates, protocol versions, cipher suites, and certificate
                chain validity. TLS drift often occurs during renewals and infrastructure changes.
                Set alerts for certificates expiring within 30 days and any protocol downgrades.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">5</div>
              <div className="step-card-title">Inventory Technology Versions</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Maintain a record of what software runs on each asset — web servers, CMS platforms,
                frameworks, and plugins. Compare detected versions against your inventory to catch
                unauthorized updates, downgrades, and end-of-life software before it becomes exploitable.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">6</div>
              <div className="step-card-title">Monitor Domain Registration Health</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Track domain expiration dates, registrar lock status, nameserver configurations, and WHOIS
                contact details. Set renewal reminders at 90, 60, and 30 days. Verify that domain ownership
                contacts are current and not tied to former employees.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">7</div>
              <div className="step-card-title">Define Drift Response Procedures</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Not all drift requires the same response. Classify drift events by severity — a new open
                management port is urgent, a minor TLS cipher change may be informational. Define who gets
                notified, what the response SLA is, and how resolved drift events are documented and closed.
              </p>
            </div>
          </div>
        </section>

        {/* Mid-article CTA */}
        <section className="guide-section">
          <div className="guide-cta-section">
            <h3>How DriftAlarm Helps</h3>
            <p>
              DriftAlarm monitors your external attack surface continuously and compares every scan against
              an automatically maintained baseline. When DNS records change, ports open, TLS degrades, or
              technology versions shift, you get an alert — not a scan report to read through manually.
            </p>
            <div className="guide-cta-buttons">
              <button onClick={startTrial} className="da-btn da-btn--primary">
                <span>Start Free 30-Day Trial</span>
                <span>&rarr;</span>
              </button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">See How It Works</Link>
            </div>
          </div>
        </section>

        {/* Section 7: How DriftAlarm Detects Drift */}
        <section id="how-driftalarm-detects" className="guide-section">
          <h2>How DriftAlarm Detects Configuration Drift</h2>
          <p>
            DriftAlarm's approach to drift detection is built on three layers: automated baselining,
            rule-based change detection, and trend analysis.
          </p>

          <h3>Automated Baselining</h3>
          <p>
            When you add an asset to DriftAlarm, the platform runs discovery and vulnerability scans to
            establish a baseline — your asset's known-good state. This baseline captures DNS records,
            open ports, running services, TLS configurations, technology stack details, and HTTP response
            behaviors. Every subsequent scan is compared against this baseline.
          </p>

          <h3>32 Drift Rules Across 7 Packs</h3>
          <p>
            DriftAlarm evaluates each scan against 32 drift detection rules organized into 7 rule packs.
            These rules cover the drift categories discussed in this guide:
          </p>
          <ul>
            <li><strong>DNS drift:</strong> New subdomains, record changes, dangling CNAMEs, nameserver modifications</li>
            <li><strong>Port drift:</strong> New open ports, closed ports reopening, service changes on existing ports</li>
            <li><strong>TLS drift:</strong> Certificate expiration, protocol downgrades, cipher suite changes, chain issues</li>
            <li><strong>Technology drift:</strong> Version changes, new technologies detected, end-of-life software</li>
            <li><strong>Registration drift:</strong> Expiration approaching, WHOIS changes, registrar lock status</li>
            <li><strong>HTTP drift:</strong> Security header changes, response code changes, redirect modifications</li>
            <li><strong>Vulnerability drift:</strong> New CVEs detected, vulnerability status changes, severity escalation</li>
          </ul>
          <p>
            Each rule fires only when a meaningful change is detected — not on every scan. This prevents
            alert fatigue while ensuring genuine drift events are captured promptly.
          </p>

          <h3>Trend Charts and Historical Tracking</h3>
          <p>
            Beyond individual alerts, DriftAlarm maintains a historical record of your attack surface
            over time. Trend charts show how your open port count, vulnerability count, TLS health, and
            overall risk score change week over week. This gives you visibility into gradual drift that
            might not trigger any single rule but represents a meaningful erosion of your security posture
            when viewed over months.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">Drift Detection Is Not Vulnerability Scanning</div>
            <div className="callout-content">
              <p>
                Vulnerability scanners find known CVEs. Drift detection finds <em>changes</em> — including
                changes that do not correspond to any CVE but still weaken your security. A new open port
                is not a CVE. A TLS downgrade is not a CVE. A dangling CNAME is not a CVE. But all three
                are drift events that create exploitable conditions. Effective security monitoring requires both.
              </p>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section id="related-guides" className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            Configuration drift touches every aspect of your external attack surface. These guides
            dive deeper into specific drift categories and monitoring practices:
          </p>

          <div className="playbook-links">
            <Link to="/guides/dns-drift-detection" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">DNS Drift Detection Guide</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/tls-ssl-drift" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">TLS/SSL Drift Monitoring Guide</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/attack-surface-monitoring" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Attack Surface Monitoring Guide</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/attack-surface-checklist" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Attack Surface Checklist</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/external-attack-surface-management" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">External Attack Surface Management</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="guide-section">
          <h2>Monitor Drift Across Your Attack Surface</h2>
          <p>
            Configuration drift does not announce itself. It accumulates quietly until something breaks
            or an attacker finds the gap. Continuous monitoring with automated baselining is the only
            reliable way to catch drift before it becomes an incident.
          </p>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/upgrade" className="da-btn da-btn--secondary">View Pricing</Link>
          </div>
        </section>
        <GuideFAQ path="/guides/configuration-drift" />
      </main>

      <PublicFooter />
    </div>
  );
}

export default ConfigurationDriftGuide;
